import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { randomString } from '../../utils/randomString';
import { IconName } from '../Icon';
import { Label } from '../Form/Label';
import Typography from '../Typography/Typography';
import { getHelperText, HelperText } from '../Form/HelperText';
import { ChipsSelectSkeletonProps, ChipsSelectSkeleton } from '../Skeleton/ChipsSelectSkeleton';
import { Uds } from '../../types';
import { Keys } from '../../utils';
import { ChipSelect } from './ChipSelect';

type CommonProps<VALUE> = Uds<{
  loadingProps?: ChipsSelectSkeletonProps;
  options: {
    label: string;
    value: VALUE;
    iconName?: IconName;
    dataTest?: string;
  }[];
  label?: string;
  loading?: boolean;
  required?: boolean;
  error?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  /*! description is visible under label */
  description?: string;
  /*! helperText is visible under chips */
  helperText?: string;
  /*! errorText is visible under chips and will replace helperText */
  errorText?: string;
  id?: string;
  className?: string;
  variant?: 'outlined' | 'default';
  size?: 'small' | 'medium';
}>;

interface MultipleProps<VALUE> extends CommonProps<VALUE> {
  multiple: true;
  value: VALUE[] | undefined;
  onSelect: (value: VALUE[]) => void;
}

interface SingleProps<VALUE> extends CommonProps<VALUE> {
  value: VALUE | undefined;
  onSelect: (value: VALUE | undefined) => void;
}

export type ChipsSelectProps<MULTI extends boolean = false, VALUE = string> = MULTI extends true
  ? MultipleProps<VALUE>
  : SingleProps<VALUE>;

export const ChipsSelect = <VALUE = string, MULTI extends boolean = false>({
  options,
  label,
  error,
  fullWidth,
  required,
  id,
  description,
  disabled,
  helperText,
  errorText,
  loading,
  dataTest = 'chips-select',
  loadingProps,
  className,
  variant,
  size = 'medium',
  ...rest
}: ChipsSelectProps<MULTI, VALUE>): ReactElement | null => {
  const multiProps = rest as unknown as MultipleProps<VALUE>;
  const singleProps = rest as unknown as SingleProps<VALUE>;

  const onSelectHandler = (value: VALUE, selected: boolean) => {
    if (multiProps.multiple) {
      const newValues = selected
        ? (multiProps.value?.filter((selectedValue) => selectedValue !== value) ?? [])
        : [...(multiProps?.value || []), value];

      multiProps.onSelect(newValues);
    } else {
      singleProps.onSelect(selected ? undefined : value);
    }
  };

  const newId = id ?? randomString();

  const handleKeyPress = (e: React.KeyboardEvent, value: VALUE, selected: boolean) => {
    if (Keys.isOneOf(e, ['enter', 'spacebar'])) {
      onSelectHandler(value, selected);
    }
  };

  return (
    <StyledWrapper
      className={className}
      data-test={dataTest}
      data-test-selected={Array.isArray(rest.value) ? rest.value.join(',') : rest.value}
    >
      {!!label && (
        <Label error={error} htmlFor={newId} required={required} style={{ marginBottom: '0' }}>
          {label}
        </Label>
      )}
      {!!description && <Typography variant="body2">{description}</Typography>}
      {!loading ? (
        <StyledChipsWrapper id={newId}>
          {options.map(({ value: optionValue, label, iconName, dataTest }) => {
            const selected = checkIfSelectedChip(rest.value, optionValue);
            return (
              <ChipSelect
                disabled={disabled}
                selected={selected}
                key={(optionValue as any)?.toString()}
                label={label}
                iconName={iconName}
                variant={variant}
                dataTest={dataTest}
                size={size}
                onClick={() => onSelectHandler(optionValue, selected)}
                onKeyDown={(e) => handleKeyPress(e, optionValue, selected)}
              />
            );
          })}
        </StyledChipsWrapper>
      ) : (
        <ChipsSelectSkeleton {...loadingProps} />
      )}
      {(!!helperText || !!errorText) && (
        <HelperText error={error}>{getHelperText(error, helperText, errorText)}</HelperText>
      )}
    </StyledWrapper>
  );
};

const StyledChipsWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacingSize.m};
  flex-wrap: wrap;
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacingSize.m};
`;

const checkIfSelectedChip = <VALUE = string>(selectedValues: VALUE | VALUE[], optionValue: VALUE) => {
  if (Array.isArray(selectedValues)) return selectedValues.includes(optionValue);
  return selectedValues === optionValue;
};
